import { Spin } from "@/components/Spin";
import { CommentTarget } from "@/enums";
import { useCreateCommentMutation, useGetTagsQuery } from "@/services";
import { Form, Input, Modal, Select, Tag } from "antd";
import { bool, func, string } from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { CommentItem } from "./styled";

const { TextArea } = Input;

export const AddCommentModal = ({
  isVisible,
  setVisible,
  projectId,
  target,
  targetId,
  tagName,
  tagDisabled,
  onFinish,
  title,
}) => {
  const [form] = Form.useForm();
  const intl = useIntl();

  const [tagOptions, setTagOptions] = useState();
  const [defaultTag, setDefaultTag] = useState();

  const [isSaving, setIsSaving] = useState(false);

  const { data: tags, isFetching: tagsLoading } = useGetTagsQuery();
  const [createComment] = useCreateCommentMutation();

  const routeParams = {
    projectId,
    target,
    targetId,
  };

  useEffect(() => {
    if (!tags?.length) return;

    setTagOptions(
      tags
        .filter((tag) => tag.targets?.includes(target))
        .map((tag) => ({ label: tag.label, value: tag.id }))
    );
  }, [tags, target]);

  useEffect(() => {
    if (!tags?.length || !tagName) return;

    const defaultValue = tags.find(
      (tag) => tag.label.toLowerCase() === tagName.toLowerCase()
    )?.id;

    if (defaultValue) setDefaultTag(defaultValue);
  }, [tagName, tags]);

  useEffect(() => {
    if (!defaultTag || tagsLoading || !isVisible) return;

    form.setFieldsValue({
      tag: [defaultTag],
    });
  }, [defaultTag, form, isVisible, tagsLoading]);

  const addComment = (commentData) => {
    createComment({ routeParams, body: commentData })
      .unwrap()
      .then(() => {
        form.resetFields();
        setVisible(false);
        toast.success(intl.formatMessage({ id: "comments.commentSuccess" }));
      })
      .catch(() => {
        toast.error(intl.formatMessage({ id: "comments.error" }));
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const tagRender = ({ value }) => {
    const tag = tags.find((tag) => tag.id === value);
    return (
      <>
        {tag && (
          <Tag value={tag.id} color={tag.color}>
            {tag.label}
          </Tag>
        )}
      </>
    );
  };

  const handleSelect = (selected) => {
    form.setFieldsValue({ tag: [selected] });
  };

  const handleOk = () => {
    setIsSaving(true);
    form.validateFields().then(
      ({ text, tag }) => {
        if (!text) return;
        const commentData = {
          tag: defaultTag || tag?.[0],
          text: text,
        };
        // If there is an onFinish action, wait for it to complete before adding the comment
        if (typeof onFinish === "function") {
          onFinish(() => {
            addComment(commentData);
          });
        } else {
          addComment(commentData);
        }
      },
      () => setIsSaving(false)
    );
  };

  return (
    <Modal
      title={
        title && title.trim()
          ? title
          : intl.formatMessage({ id: "comments.addComment" })
      }
      open={isVisible}
      centered={true}
      onOk={() => handleOk()}
      okText={intl.formatMessage({ id: "comments.post" })}
      okButtonProps={{
        loading: isSaving,
      }}
      onCancel={() => {
        setVisible(false);
        form.resetFields();
      }}
      cancelButtonProps={{
        disabled: isSaving,
      }}
      cancelText={intl.formatMessage({ id: "general.cancel" })}
    >
      {!tagsLoading ? (
        <Form
          form={form}
          layout="vertical"
          name="comment-form"
          disabled={isSaving}
        >
          {target !== CommentTarget.Milestone && (
            <CommentItem
              name="tag"
              label={intl.formatMessage({ id: "comments.tag" })}
            >
              <Select
                data-test-id="add-comment-select-tag"
                mode="tags"
                placeholder={intl.formatMessage({ id: "general.select" })}
                tagRender={tagRender}
                options={tagOptions}
                disabled={tagDisabled}
                onSelect={handleSelect}
              />
            </CommentItem>
          )}
          <CommentItem
            name="text"
            label={intl.formatMessage({ id: "comments.comment" })}
            rules={[
              {
                required: true,
                message: intl.formatMessage({ id: "comments.noValue" }),
              },
            ]}
          >
            <TextArea rows={4} />
          </CommentItem>
        </Form>
      ) : (
        <>
          <Form form={form} component={false} />
          <Spin />
        </>
      )}
    </Modal>
  );
};

AddCommentModal.propTypes = {
  isVisible: bool.isRequired,
  setVisible: func.isRequired,
  projectId: string.isRequired,
  target: string.isRequired,
  targetId: string,
  tagName: string,
  tagDisabled: bool,
  onSuccessfulAdd: func,
  onFinish: func,
  title: string,
};
